import type { MaybeRefOrGetter } from 'vue';
import type { SelectFieldOption } from '#layers/data-model';
import { formatTitle } from '@directus/format-title';
import { computed, toValue } from 'vue';
import { useTranslationUtils } from '#layers/i18n';

interface UseSelectOptionsParameters {
	/**
	 * Translate the label automatically
	 *
	 * @default true
	 */
	translate?: boolean;
	/**
	 * Convert the value to title case when no explicit label is provided
	 *
	 * @default true
	 */
	autoTitleCaseValue?: boolean;
}

export function useSelectOptions<T = SelectFieldOption>(options?: MaybeRefOrGetter<SelectFieldOption[] | undefined>, params: UseSelectOptionsParameters = {}) {
	const { translate = true, autoTitleCaseValue = true } = params;

	const { translateString } = useTranslationUtils();

	const normalizedOptions = computed(() => (toValue(options) ?? [])
		.map((option) => {
			const hasLabel = !!option.label;
			let label = option.label || String(option.value);
			let description = option.description;

			if (translate) {
				label = translateString(label);
				description = description ? translateString(description) : undefined;
			}

			if (autoTitleCaseValue && !hasLabel) {
				label = formatTitle(label);
			}

			return ({ ...option, label, description });
		})
		.filter((option) => option.value !== undefined) as T[]);

	return { normalizedOptions };
}
